import type {
  CancelCommerceIntentDto,
  CommerceIntentDto,
  CommerceQuoteDto,
  CommerceTargetRefDto,
  ConfirmCommerceIntentDto,
  CreateCommerceIntentDto,
  CreateCommerceQuoteDto,
} from "@zipper/customer-commerce-client";
import {
  Configuration,
  PublicCommerceApi,
  type CreatePublicCommerceTargetDraftDto,
  type CommerceProgramDiscoveryDto,
} from "@zipper/public-api-client";
import type {
  CheckoutGateway,
  CheckoutRuntimeIdentity,
  ResolvedCheckoutTarget,
} from "../checkout/actors";
import { getApiBase } from "../config/client";
import { ScopedSessionStore } from "../state/session-store";
import {
  ZIPPER_EMBED_BUNDLE_ID,
  ZIPPER_EMBED_BUNDLE_VERSION,
} from "../version";

export interface PublicCommerceIdentity {
  orgId: number;
  audience: string;
  subjectToken: string;
  target: CommerceTargetRefDto;
}

export interface DashboardCommercePort {
  listTargets(input: {
    orgId: number;
    signal: AbortSignal;
  }): Promise<readonly CommerceTargetRefDto[]>;
  quote(input: {
    orgId: number;
    request: CreateCommerceQuoteDto;
    signal: AbortSignal;
  }): Promise<CommerceQuoteDto>;
  createIntent(input: {
    orgId: number;
    request: CreateCommerceIntentDto;
    signal: AbortSignal;
  }): Promise<CommerceIntentDto>;
  confirmIntent(input: {
    orgId: number;
    request: ConfirmCommerceIntentDto;
    signal: AbortSignal;
  }): Promise<CommerceIntentDto>;
  getIntent(input: {
    orgId: number;
    intentId: string;
    signal: AbortSignal;
  }): Promise<CommerceIntentDto>;
  cancelIntent(input: {
    orgId: number;
    request: CancelCommerceIntentDto;
    signal: AbortSignal;
  }): Promise<CommerceIntentDto>;
}

const commerceScope = (orgId: number, audience: string) => ({
  orgId,
  audience: `public-commerce:${audience}` as const,
});

export function readPublicCommerceSubject(
  store: ScopedSessionStore,
  orgId: number,
  audience: string,
): string | null {
  return store.read(commerceScope(orgId, audience))?.token ?? null;
}

export function clearPublicCommerceSubject(
  store: ScopedSessionStore,
  orgId: number,
  audience: string,
): void {
  store.clear(commerceScope(orgId, audience));
}

function publicClient(input: {
  audience: string;
  subjectToken?: string;
}): PublicCommerceApi {
  return new PublicCommerceApi(
    new Configuration({
      basePath: getApiBase(),
      headers: {
        Accept: "application/json",
        "X-Zipper-Client": ZIPPER_EMBED_BUNDLE_ID,
        "X-Zipper-Client-Version": ZIPPER_EMBED_BUNDLE_VERSION,
        "X-Commerce-Audience": input.audience,
        ...(input.subjectToken
          ? { "X-Commerce-Subject": input.subjectToken }
          : {}),
      },
    }),
  );
}

export async function createPublicCommerceIdentity(input: {
  orgId: number;
  audience: string;
  draft: Omit<CreatePublicCommerceTargetDraftDto, "audience" | "website">;
  store: ScopedSessionStore;
  signal?: AbortSignal;
}): Promise<PublicCommerceIdentity> {
  const result = await publicClient({
    audience: input.audience,
  }).createTargetDraft(
    {
      orgId: input.orgId,
      createPublicCommerceTargetDraftDto: {
        ...input.draft,
        audience: input.audience,
      },
    },
    input.signal ? { signal: input.signal } : undefined,
  );
  input.store.write({
    scope: commerceScope(input.orgId, input.audience),
    token: result.subjectToken,
  });
  return {
    orgId: input.orgId,
    audience: input.audience,
    subjectToken: result.subjectToken,
    target: result.target,
  };
}

export function discoverCommercePrograms(
  orgId: number,
  signal?: AbortSignal,
): Promise<CommerceProgramDiscoveryDto[]> {
  return publicClient({
    audience: `program-discovery:${orgId}`,
  }).discoverPrograms({ orgId }, signal ? { signal } : undefined);
}

export function createPublicCommerceGateway(
  identity: PublicCommerceIdentity,
): CheckoutGateway {
  const api = publicClient({
    audience: identity.audience,
    subjectToken: identity.subjectToken,
  });
  const assertRuntime = (runtime: CheckoutRuntimeIdentity) => {
    if (
      runtime.surface !== "public-embed" ||
      runtime.orgId !== identity.orgId ||
      runtime.audience !== identity.audience
    ) {
      throw new TypeError(
        "Public commerce identity does not match the checkout runtime",
      );
    }
  };
  return {
    async resolveTarget({ runtime, requestedTargetKey }) {
      assertRuntime(runtime);
      if (
        requestedTargetKey &&
        requestedTargetKey !== identity.target.targetKey
      ) {
        throw new Error("Public commerce target is not authorized");
      }
      return {
        targetKey: identity.target.targetKey,
        participantSelectionKeys: [],
        revision: identity.target.revision,
        selectedTarget: identity.target,
        availableTargets: [identity.target],
      };
    },
    async quote({ runtime, request, signal }) {
      assertRuntime(runtime);
      return api.quote(
        {
          orgId: identity.orgId,
          createCommerceQuoteDto: {
            ...request,
            targetKey: identity.target.targetKey,
          },
        },
        { signal },
      ) as Promise<CommerceQuoteDto>;
    },
    async createIntent({ runtime, request, signal }) {
      assertRuntime(runtime);
      return api.createIntent(
        {
          orgId: identity.orgId,
          createCommerceIntentDto: request,
        },
        { signal },
      ) as Promise<CommerceIntentDto>;
    },
    async confirmIntent({ runtime, request, signal }) {
      assertRuntime(runtime);
      return api.confirmIntent(
        {
          orgId: identity.orgId,
          confirmCommerceIntentDto: request,
        },
        { signal },
      ) as Promise<CommerceIntentDto>;
    },
    async recoverIntent({ runtime, intentId, signal }) {
      assertRuntime(runtime);
      return api.getIntent(
        { orgId: identity.orgId, intentId },
        { signal },
      ) as Promise<CommerceIntentDto>;
    },
    async cancelIntent({ runtime, request, signal }) {
      assertRuntime(runtime);
      return api.cancelIntent(
        {
          orgId: identity.orgId,
          cancelCommerceIntentDto: request,
        },
        { signal },
      ) as Promise<CommerceIntentDto>;
    },
  };
}

export function createDashboardCommerceGateway(
  port: DashboardCommercePort,
): CheckoutGateway {
  const assertRuntime = (runtime: CheckoutRuntimeIdentity) => {
    if (runtime.surface !== "dashboard-customer") {
      throw new TypeError("Dashboard gateway requires dashboard runtime");
    }
  };
  const target = async (
    runtime: CheckoutRuntimeIdentity,
    requestedTargetKey: string | null,
    signal: AbortSignal,
  ): Promise<ResolvedCheckoutTarget> => {
    assertRuntime(runtime);
    const targets = await port.listTargets({
      orgId: runtime.orgId,
      signal,
    });
    const selected = requestedTargetKey != null
      ? targets.find(({ targetKey }) => targetKey === requestedTargetKey)
      : targets.find(({ kind }) => kind === "self") ?? targets[0];
    if (requestedTargetKey != null && !selected)
      throw new Error("The selected participant is no longer available. Please choose again.");
    if (!selected)
      throw new Error("No authorized commerce target is available");
    return {
      targetKey: selected.targetKey,
      participantSelectionKeys: [],
      revision: selected.revision,
      selectedTarget: selected,
      availableTargets: targets,
    };
  };
  return {
    resolveTarget: ({ runtime, requestedTargetKey, signal }) =>
      target(runtime, requestedTargetKey, signal),
    quote: ({ runtime, request, signal }) => {
      assertRuntime(runtime);
      return port.quote({ orgId: runtime.orgId, request, signal });
    },
    createIntent: ({ runtime, request, signal }) => {
      assertRuntime(runtime);
      return port.createIntent({ orgId: runtime.orgId, request, signal });
    },
    confirmIntent: ({ runtime, request, signal }) => {
      assertRuntime(runtime);
      return port.confirmIntent({ orgId: runtime.orgId, request, signal });
    },
    recoverIntent: ({ runtime, intentId, signal }) => {
      assertRuntime(runtime);
      return port.getIntent({ orgId: runtime.orgId, intentId, signal });
    },
    cancelIntent: ({ runtime, request, signal }) => {
      assertRuntime(runtime);
      return port.cancelIntent({ orgId: runtime.orgId, request, signal });
    },
  };
}
